import React, {useEffect, useState} from "react";
import API from "../../helpers/Axios";
import AddStudioAdModal from "../../components/AddStudioAdModal";

const normalizeList = (payload) => {
	if (Array.isArray(payload)) return payload;
	if (Array.isArray(payload?.data)) return payload.data;
	if (Array.isArray(payload?.items)) return payload.items;
	return [];
};

const toNumberOr = (value, fallback = 0) => {
	const n = Number(value);
	return Number.isFinite(n) ? n : fallback;
};

function StudioAdsScreen() {
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [error, setError] = useState("");
	const [studioAds, setStudioAds] = useState([]);
	const [isAddOpen, setIsAddOpen] = useState(false);
	const [isEditOpen, setIsEditOpen] = useState(false);
	const [editingAd, setEditingAd] = useState(null);
	const [studioAdFormErrors, setStudioAdFormErrors] = useState({});
	const [editStudioAdFormErrors, setEditStudioAdFormErrors] = useState({});

	const loadStudioAds = async () => {
		setIsLoading(true);
		setError("");
		try {
			const adsRes = await API.get("admin/studio-ads");
			setStudioAds(normalizeList(adsRes.data));
		} catch (e) {
			setError("Unable to load studio ads.");
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		loadStudioAds();
	}, []);

	const addStudioAd = async (payload) => {
		setIsSaving(true);
		setError("");
		setStudioAdFormErrors({});
		try {
			const data = new FormData();
			data.append("studio_id", payload.studio_id ? toNumberOr(payload.studio_id) : "");
			data.append("slot_id", toNumberOr(payload.slot_id));
			(payload.slot_ids || []).forEach((slotId) => data.append("slot_ids[]", toNumberOr(slotId)));
			data.append("title", payload.title);
			data.append("destination_url", payload.destination_url || "");
			data.append("cover", payload.cover);
			data.append("priority", toNumberOr(payload.priority, 10));
			data.append("sort_order", toNumberOr(payload.sort_order, 1));
			data.append("display_seconds", toNumberOr(payload.display_seconds, 10));
			data.append("active", payload.active ? "1" : "0");

			await API.post("admin/studio-ads/create", data, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			});
			setIsAddOpen(false);
			setStudioAdFormErrors({});
			await loadStudioAds();
		} catch (e) {
			const validationErrors = e?.response?.data?.errors;
			if (validationErrors && typeof validationErrors === "object") {
				setStudioAdFormErrors(validationErrors);
			} else {
				setError("Unable to create studio ad.");
			}
		} finally {
			setIsSaving(false);
		}
	};

	const updateStudioAd = async (payload) => {
		setIsSaving(true);
		setError("");
		setEditStudioAdFormErrors({});
		try {
			const data = new FormData();
			data.append("id", toNumberOr(payload.id));
			data.append("studio_id", payload.studio_id ? toNumberOr(payload.studio_id) : "");
			data.append("slot_id", toNumberOr(payload.slot_id));
			(payload.slot_ids || []).forEach((slotId) => data.append("slot_ids[]", toNumberOr(slotId)));
			data.append("title", payload.title);
			data.append("destination_url", payload.destination_url || "");
			if (payload.cover) data.append("cover", payload.cover);
			data.append("priority", toNumberOr(payload.priority, 10));
			data.append("sort_order", toNumberOr(payload.sort_order, 1));
			data.append("display_seconds", toNumberOr(payload.display_seconds, 10));
			data.append("active", payload.active ? "1" : "0");

			await API.post("admin/studio-ads/update", data, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			});
			setIsEditOpen(false);
			setEditingAd(null);
			setEditStudioAdFormErrors({});
			await loadStudioAds();
		} catch (e) {
			const validationErrors = e?.response?.data?.errors;
			if (validationErrors && typeof validationErrors === "object") {
				setEditStudioAdFormErrors(validationErrors);
			} else {
				setError("Unable to update studio ad.");
			}
		} finally {
			setIsSaving(false);
		}
	};

	const toggleAd = async (item) => {
		const isActive = Boolean(item.active ?? item.is_active);
		setIsSaving(true);
		setError("");
		try {
			await API.post("admin/studio-ads/update", {
				id: item.id,
				title: item.title || "",
				priority: item.priority ?? 0,
				sort_order: item.sort_order ?? 0,
				active: !isActive,
			});
			await loadStudioAds();
		} catch (e) {
			setError("Unable to update studio ad.");
		} finally {
			setIsSaving(false);
		}
	};

	const deleteAd = async (item) => {
		const confirmed = window.confirm(`Delete studio ad #${item.id} (${item.title || "Untitled ad"})?`);
		if (!confirmed) return;

		setIsSaving(true);
		setError("");
		try {
			await API.post("admin/studio-ads/delete", {
				id: item.id,
			});
			await loadStudioAds();
		} catch (e) {
			setError("Unable to delete studio ad.");
		} finally {
			setIsSaving(false);
		}
	};

	const statusPill = (isActive) => (
		<span
			className={`rounded-full px-3 py-1 text-xs font-semibold ${
				isActive ? "bg-[#E8F8EF] text-[#198754]" : "bg-[#F3F4F6] text-[#6D6E6F]"
			}`}
		>
			{isActive ? "Active" : "Inactive"}
		</span>
	);

	const mobileToggle = (isActive, onClick) => (
		<button
			type="button"
			onClick={onClick}
			disabled={isSaving}
			className={`relative inline-flex h-7 w-12 items-center rounded-full transition disabled:opacity-60 ${
				isActive ? "bg-[#31A762]" : "bg-[#D1D5DB]"
			}`}
			aria-pressed={isActive}
		>
			<span className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${isActive ? "translate-x-6" : "translate-x-1"}`} />
		</button>
	);

	return (
		<div className="flex-1 p-10">
			<AddStudioAdModal
				isOpen={isAddOpen}
				isSaving={isSaving}
				onClose={() => {
					setIsAddOpen(false);
					setStudioAdFormErrors({});
				}}
				onSubmit={addStudioAd}
				serverErrors={studioAdFormErrors}
				onClearServerErrors={() => setStudioAdFormErrors({})}
			/>
			<AddStudioAdModal
				isOpen={isEditOpen}
				isSaving={isSaving}
				onClose={() => {
					setIsEditOpen(false);
					setEditingAd(null);
					setEditStudioAdFormErrors({});
				}}
				onSubmit={updateStudioAd}
				modalTitle="Add ad"
				modalDescription="Create or update ad details."
				submitLabel="Save changes"
				initialValues={editingAd}
				requireCover={false}
				serverErrors={editStudioAdFormErrors}
				onClearServerErrors={() => setEditStudioAdFormErrors({})}
			/>

			<div className="mb-8 flex flex-col items-start justify-between gap-4 xl:flex-row xl:items-center">
				<div>
					<h1 className="text-5xl font-semibold">Ads</h1>
					<p className="mt-4 text-xl text-[#595959]">Manage studio ads from Admin API.</p>
				</div>
				<div className="flex items-center gap-2">
					<button
						type="button"
						onClick={() => setIsAddOpen(true)}
						disabled={isSaving}
						className="inline-flex items-center gap-2 rounded-md border border-[#E4E1E0] bg-white px-4 py-2 text-sm font-semibold text-[#1E1F21] transition hover:bg-[#F8F8F8] disabled:opacity-60"
					>
						<svg width={16} height={16} fill="none" xmlns="http://www.w3.org/2000/svg">
							<path d="M8 3.333v9.334M3.333 8h9.334" stroke="#1E1F21" strokeWidth={1.5} strokeLinecap="round" />
							<rect x={1.75} y={1.75} width={12.5} height={12.5} rx={2.25} stroke="#1E1F21" strokeWidth={1.5} />
						</svg>
						Add studio ad
					</button>
				</div>
			</div>

			{error ? (
				<div className="mb-6 rounded-md border border-[#F5C2C7] bg-[#FBEAEC] px-4 py-3 text-sm text-[#842029]">
					{error}
				</div>
			) : null}

			<div className="mb-4 text-xl font-semibold text-[#1E1F21]">All studio ads ({studioAds.length})</div>
			<section className="">
				<div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
					{studioAds.map((item) => {
						const isActive = Boolean(item.active ?? item.is_active);
						const displayTitle = item.title || "Untitled ad";
						return (
							<div key={item.id} className="overflow-hidden rounded-xl border border-[#E8E8E8] bg-white shadow-[0_6px_20px_rgba(0,0,0,0.05)]">
								<div className="relative h-40 w-full bg-[#EEF0F2]">
									{item.cover_url ? (
										<img src={item.cover_url} alt={displayTitle} className="h-full w-full object-cover" />
									) : (
										<div className="flex h-full w-full items-center justify-center text-sm font-medium text-[#6D6E6F]">No cover</div>
									)}
									<div className="absolute right-3 top-3">{statusPill(isActive)}</div>
								</div>
								<div className="space-y-3 p-4">
									<div>
										<p className="text-base font-semibold text-[#1E1F21]">{displayTitle}</p>
										<p className="mt-1 text-sm text-[#6D6E6F]">
											ID: {item.id} • Priority: {item.priority ?? 0} • Sort: {item.sort_order ?? 0}
										</p>
									</div>
									<div className="flex items-center justify-between gap-3">
										{mobileToggle(isActive, () => toggleAd(item))}
										<div className="flex items-center gap-2">
											<button
												type="button"
												onClick={() => {
													setEditingAd(item);
													setEditStudioAdFormErrors({});
													setIsEditOpen(true);
												}}
												disabled={isSaving}
												className="rounded-md border border-[#D5D5D5] px-3 py-1.5 text-sm font-medium text-[#6D6E6F] transition hover:bg-[#F8F8F8] disabled:opacity-60"
											>
												Edit
											</button>
											<button
												type="button"
												onClick={() => deleteAd(item)}
												disabled={isSaving}
												className="rounded-md border border-[#DC3545] bg-white px-3 py-1.5 text-sm font-semibold text-[#DC3545] transition hover:bg-[#FDF2F4] disabled:opacity-60"
											>
												Delete
											</button>
										</div>
									</div>
								</div>
							</div>
						);
					})}
					{studioAds.length === 0 && !isLoading ? (
						<p className="text-sm text-[#6D6E6F]">No studio ads found.</p>
					) : null}
				</div>
			</section>
		</div>
	);
}

export default StudioAdsScreen;
